import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {NavBar} from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { motion, useScroll, useTransform } from "framer-motion";
import ComplianceAIForm from "@/components/compliance-ai/ComplianceAIForm";
import ComplianceAIResponse from "@/components/compliance-ai/ComplianceAIResponse";
import { ComplianceFormData } from "@/types/compliance";
import { mockComplianceAiResponse } from "@/mocks/complianceAiResponse";
import { API_CONFIG } from "@/constants/env.ts";
import { 
  CheckCircle, Clock, FileCheck, BarChart3, Shield, Briefcase, Brain,
  ArrowRight, MessageCircle, Zap, CheckCircle2, Building, LightbulbIcon, 
  ExternalLink, Play, MoveRight, ChevronRight
} from "lucide-react";

// Verc Landing Page for Financial Marketing Compliance
const VercLanding = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);

    try {
      // Use the actual API
      const apiUrl = `${window.location.origin}/compliance-ai`;
      console.log("Sending request to:", apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
      }
      
      let responseData = await response.json();
      
      console.log("API Response:", responseData);
      
      // Process the response if needed
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          responseData = { content: responseData };
        }
      }
      
      setResponse(responseData);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error instanceof Error) {
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }
      
      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log("Falling back to mock data due to API error");
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: "Finance Marketing Compliance Assessment",
            summary: "Your financial marketing materials have been analyzed for regulatory compliance. Here are our recommendations to improve your compliance posture."
          });
        }, 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Regulatory frameworks covered
  const regulations = [
    { name: "SEC", description: "Securities and Exchange Commission" },
    { name: "FINRA", description: "Financial Industry Regulatory Authority" },
    { name: "MiFID II", description: "Markets in Financial Instruments Directive" },
    { name: "AIFMD", description: "Alternative Investment Fund Managers Directive" },
    { name: "FCA", description: "Financial Conduct Authority" },
    { name: "ESMA", description: "European Securities and Markets Authority" },
    { name: "GDPR", description: "General Data Protection Regulation" },
    { name: "CCPA", description: "California Consumer Privacy Act" }
  ];

  // Key features cards
  const keyFeatures = [
    {
      title: "Compliance Automation",
      description: "Our AI scans marketing materials for compliance issues in seconds, identifying potential regulatory violations before they become problems.",
      icon: <Brain className="h-10 w-10 text-emerald-500" />
    },
    {
      title: "Multi-Channel Monitoring",
      description: "Monitor all marketing channels simultaneously - websites, social media, emails, and promotional materials - to ensure total compliance.",
      icon: <Shield className="h-10 w-10 text-emerald-500" />
    },
    {
      title: "Accelerated Reviews",
      description: "Reduce compliance review cycles from days to minutes with our AI-powered pre-screening that highlights only genuine compliance issues.",
      icon: <BarChart3 className="h-10 w-10 text-emerald-500" />
    }
  ];

  // Benefits
  const benefits = [
    {
      title: "95% Faster Compliance Reviews",
      description: "Our AI analyzes marketing materials in seconds instead of days, dramatically accelerating your compliance workflow.",
      icon: <Clock className="h-6 w-6 text-emerald-500" />
    },
    {
      title: "90% Accuracy Rate",
      description: "Trained on the latest financial regulations, our AI delivers human-level accuracy for compliance checks with machine speed.",
      icon: <Shield className="h-6 w-6 text-emerald-500" />
    },
    {
      title: "Cross-Border Expertise",
      description: "Navigate global marketing compliance with region-specific regulatory checks for different jurisdictions.",
      icon: <FileCheck className="h-6 w-6 text-emerald-500" />
    },
    {
      title: "Audit-Ready Documentation",
      description: "Generate comprehensive compliance records with one click, making regulatory inspections straightforward.",
      icon: <Briefcase className="h-6 w-6 text-emerald-500" />
    }
  ];

  // FAQ items
  const faqItems = [
    {
      question: "How accurate is your AI compliance analysis?",
      answer: "Our AI achieves a 90% accuracy rate for financial marketing compliance analysis, comparable to expert human reviewers but delivered in a fraction of the time."
    },
    {
      question: "Which regulatory frameworks are covered?",
      answer: "Our platform covers major financial regulations including SEC, FINRA, MiFID II, AIFMD, FCA guidelines, ESMA directives, and more. We continuously update our AI with the latest regulatory changes."
    },
    {
      question: "How does the platform integrate with our existing workflows?",
      answer: "Verdea AI offers seamless integration with popular marketing tools, CMS platforms, and compliance management systems. Our API allows for custom integrations with your existing tech stack."
    },
    {
      question: "What about data security and privacy?",
      answer: "We maintain the highest standards of data security with GDPR compliance, end-to-end encryption, and secure cloud infrastructure. Your sensitive financial information never leaves our secure environment."
    }
  ];

  // Testimonials from financial institutions
  const testimonials = [
    {
      quote: "Verdea has reduced our compliance review cycle from 5 days to just hours, enabling our marketing team to be more agile without sacrificing regulatory compliance.",
      author: "Sarah Johnson",
      title: "Chief Compliance Officer",
      company: "Global Investment Partners"
    },
    {
      quote: "The platform's ability to spot potential compliance issues before content even reaches our review team has been transformative for our workflow efficiency.",
      author: "Michael Chen",
      title: "Head of Marketing",
      company: "Meridian Financial Services"
    },
    {
      quote: "Implementing Verdea has reduced our compliance-related marketing delays by 85% while improving our regulatory adherence across multiple jurisdictions.",
      author: "Elizabeth Torres",
      title: "VP of Regulatory Affairs",
      company: "Atlantic Trust Bank"
    }
  ];

  return (
    <div className="relative min-h-screen">
      <Helmet>
        <title>Financial Marketing Compliance Platform | Verdea AI</title>
        <meta 
          name="description" 
          content="Transform your financial marketing compliance with AI. Streamline reviews and ensure regulatory compliance across all marketing channels." 
        />
      </Helmet>
      
      {/* Navbar - using darker styling when scrolled */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <NavBar landingMode={true} />
      </header>
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-40 overflow-hidden bg-gradient-to-b from-emerald-50 to-white">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 -right-10 w-[600px] h-[600px] opacity-20 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="absolute bottom-0 -left-10 w-[600px] h-[600px] opacity-10 rounded-full bg-emerald-300/30 blur-3xl" />
            
            {/* Grid pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmMGYwZjAiIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBoMzB2MzBIMzB6TTAgMGgzMHYzMEgweiIgZmlsbC1vcGFjaXR5PSIuMDMiIGZpbGw9IiMwMDAiLz48L2c+PC9zdmc+')] opacity-30" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left column - Text content */}
              <div className="max-w-2xl">
                <div className="flex items-center mb-6 space-x-2">
                  <span className="px-3 py-1 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">NEW</span>
                  <span className="text-sm text-gray-600">Modular AI Regulatory Tech Platform</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight">
                  <span className="block">Marketing Compliance</span>
                  <span className="bg-gradient-to-r from-emerald-600 to-green-500 bg-clip-text text-transparent">for Financial Services</span>
                </h1>
                
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Streamline compliance reviews, accelerate time-to-market, and mitigate regulatory risks with our modular AI regulatory technology platform for financial institutions.
                </p>
                
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button 
                    size="lg" 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 shadow-lg shadow-emerald-200 flex items-center gap-2"
                    onClick={() => {
                      document.getElementById('compliance-form')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <span>Try Free Analysis</span>
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                  
                  <a href="#how-it-works" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-800 font-medium px-4 py-2.5">
                    <Play className="h-5 w-5" />
                    <span>How it works</span>
                  </a>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-emerald-600">95%</span>
                    <span className="text-sm text-gray-600">Faster Reviews</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-emerald-600">90%</span>
                    <span className="text-sm text-gray-600">Accuracy</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold text-emerald-600">8+</span>
                    <span className="text-sm text-gray-600">Regulations</span>
                  </div>
                </div>
              </div>
              
              {/* Right column - Dashboard preview */}
              <div className="rounded-2xl shadow-2xl shadow-emerald-200 relative bg-white p-2">
                <div className="rounded-xl overflow-hidden">
                  <img 
                    src="/screens/dashboard_overview.png" 
                    alt="Financial Marketing Compliance Dashboard" 
                    className="w-full h-auto"
                  />
                </div>
                
                {/* Floating stats cards */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-4 max-w-[200px]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Compliance Rate</p>
                      <p className="text-xl font-bold text-green-600">98.2%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Logos Section */}
        <section className="py-12 border-y border-gray-200">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <p className="text-gray-600 text-sm font-medium mb-4">TRUSTED BY LEADING FINANCIAL INSTITUTIONS</p>
              <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
                <img src="/logos/vba.png" alt="VBA" className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/ant.png" alt="Ant Financial" className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/mm1.png" alt="MM1" className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/ejaso.png" alt="Ejaso" className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                <img src="/logos/greentech.jpg" alt="GreenTech" className="h-7 md:h-8 opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
              </div>
            </div>
          </div>
        </section>
        
        {/* How It Works Section */}
        <section id="how-it-works" className="py-24 bg-white relative">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">How Our AI Compliance Platform Works</h2>
              <p className="text-xl text-gray-600">
                Our platform transforms how financial institutions handle marketing compliance, saving time while reducing risk.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  title: "Upload Marketing Content",
                  description: "Submit your marketing materials through our secure platform - websites, ads, social media, or brochures.",
                  icon: <FileCheck className="h-10 w-10 text-emerald-500" />,
                  step: "01"
                },
                {
                  title: "AI-Powered Analysis",
                  description: "Our specialized AI reviews content against relevant regulatory frameworks and compliance standards.",
                  icon: <Brain className="h-10 w-10 text-emerald-500" />,
                  step: "02"
                },
                {
                  title: "Receive Detailed Report",
                  description: "Get actionable recommendations, risk assessments, and suggested revisions to ensure compliance.",
                  icon: <BarChart3 className="h-10 w-10 text-emerald-500" />,
                  step: "03"
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-xl shadow-emerald-100 h-full flex flex-col">
                    <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <div className="mb-6">{step.icon}</div>
                    <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Demo Form Section */}
        <section id="compliance-form" className="py-24 bg-gray-50 relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-20 rounded-full bg-emerald-300/30 blur-3xl" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="inline-block px-3 py-1 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full mb-4">FREE ANALYSIS</span>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try Our Financial Marketing Compliance AI</h2>
                  <p className="text-xl text-gray-600 mb-6">
                    Get a free compliance analysis of your financial marketing content. No credit card required.
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    {[
                      "AI-powered compliance check against major financial regulations",
                      "Detailed report with compliance gaps and recommendations",
                      "Risk assessment based on regulatory frameworks",
                      "Actionable suggestions to improve compliance"
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                  {!response ? (
                    <>
                      <h3 className="text-2xl font-bold mb-6">Tell us about your marketing compliance challenges</h3>
                      <ComplianceAIForm 
                        onSubmit={handleFormSubmit} 
                        isLoading={isLoading}
                        error={error}
                      />
                    </>
                  ) : (
                    <ComplianceAIResponse 
                      response={response} 
                      isLoading={isLoading} 
                      error={error}
                      onReset={() => {
                        setFormData(null);
                        setResponse(null);
                        setError(null);
                      }} 
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Features Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Key Features</h2>
              <p className="text-xl text-gray-600">
                Our platform transforms how financial institutions handle marketing compliance, saving time while reducing risk.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {keyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg"
                >
                  <div className="mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Regulatory Frameworks Section */}
        <section className="py-24 bg-gradient-to-b from-emerald-50 to-white relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] opacity-20 rounded-full bg-emerald-300/30 blur-3xl" />
            
            {/* Grid pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmMGYwZjAiIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBoMzB2MzBIMzB6TTAgMGgzMHYzMEgweiIgZmlsbC1vcGFjaXR5PSIuMDMiIGZpbGw9IiMwMDAiLz48L2c+PC9zdmc+')] opacity-30" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Comprehensive Regulatory Coverage</h2>
              <p className="text-xl text-gray-600">
                Our platform helps financial institutions maintain compliance across multiple regulatory frameworks and jurisdictions.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {regulations.map((regulation, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-xl border border-emerald-100 shadow-lg shadow-emerald-100/20"
                >
                  <h3 className="font-bold text-emerald-700 mb-2">{regulation.name}</h3>
                  <p className="text-sm text-gray-600">{regulation.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Testimonials Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">What Our Clients Say</h2>
              <p className="text-xl text-gray-600">
                Financial institutions trust our platform to streamline their compliance processes.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-8 border border-gray-100 shadow-xl"
                >
                  {/* Quote symbol */}
                  <div className="mb-6 text-emerald-200">
                    <svg width="45" height="36" viewBox="0 0 45 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 0H0V13.5C0 21 6 27 13.5 27V36C6 36 0 30 0 22.5V18H4.5C9 18 13.5 13.5 13.5 9V0ZM40.5 0H27V13.5C27 21 33 27 40.5 27V36C33 36 27 30 27 22.5V18H31.5C36 18 40.5 13.5 40.5 9V0Z" fill="currentColor"/>
                    </svg>
                  </div>
                  
                  <p className="text-gray-700 mb-6 text-lg leading-relaxed">{testimonial.quote}</p>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Building className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{testimonial.author}</p>
                      <p className="text-sm text-gray-600">{testimonial.title}, {testimonial.company}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Benefits Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Key Benefits</h2>
              <p className="text-xl text-gray-600">
                Why financial institutions choose our AI-powered marketing compliance platform
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-xl flex items-start gap-5 shadow-lg"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">
                Common questions about our financial marketing compliance platform
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto">
              <div className="space-y-6">
                {faqItems.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <details className="group">
                      <summary className="flex justify-between items-center p-6 cursor-pointer">
                        <h3 className="text-lg font-medium">{faq.question}</h3>
                        <div className="ml-4 flex-shrink-0 transition-transform duration-300 group-open:rotate-180">
                          <ChevronRight className="h-5 w-5 text-gray-500" />
                        </div>
                      </summary>
                      <div className="px-6 pb-6 text-gray-600">
                        <p>{faq.answer}</p>
                      </div>
                    </details>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Final CTA */}
        <section className="py-24 bg-gradient-to-br from-emerald-600 to-green-700 text-white relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-20 rounded-full bg-white blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] opacity-10 rounded-full bg-white blur-3xl" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Transform Your Financial Marketing Compliance</h2>
              <p className="text-xl opacity-90 mb-10 max-w-3xl mx-auto">
                Get your free compliance analysis today and discover how AI can help your team achieve faster, more accurate regulatory compliance.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-white text-emerald-700 hover:bg-gray-100 rounded-full px-8 py-6 shadow-lg shadow-emerald-800/20 flex items-center gap-2"
                  onClick={() => {
                    document.getElementById('compliance-form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <span>Get Your Free Analysis</span>
                  <ArrowRight className="h-5 w-5" />
                </Button>
                
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 flex items-center gap-2"
                  onClick={() => {
                    window.open('/contact', '_blank');
                  }}
                >
                  <MessageCircle className="h-5 w-5" />
                  <span>Talk to Sales</span>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default VercLanding;